import { Platform } from 'react-native';
import * as SecureStore from 'expo-secure-store';
import {
  readWebStorage,
  removeWebStorage,
  writeWebStorage,
} from './web-storage';

/**
 * Where the app keeps the resume-handoff token a website funnel handed it
 * (`/resume-import?handoff=<token>`), until the onboarding ai-input step or
 * the /resume-import screen claims it.
 *
 * Kept free of the API client on purpose: the auth boundaries that flush the
 * token (`app-auth.ts` sign-out, `auth-handoff.ts` sign-in commit) sit
 * underneath `lib/api.ts`, so importing the claim module from them would be an
 * import cycle. `lib/resume-handoff.ts` re-exports everything here.
 *
 * Why the value is a dated record and not the bare token (incident 2026-10-04):
 * on iOS the Keychain item survives uninstall/reinstall and is shared by every
 * build of the bundle id, so a token saved months ago under another account
 * was still "pending" for a brand-new user and was claimed on mere presence.
 * Every read now ages the entry and discards it unread when it is older than
 * the longest TTL a funnel creates, or past the expiry the server reported.
 */
const RESUME_HANDOFF_TOKEN_KEY = 'tabbio_resume_handoff_token';
const RESUME_HANDOFF_TOKEN_REGEX = /^[a-f0-9]{64}$/;
/**
 * How long a stored token is trusted without a server expiry: the longest
 * handoff TTL any funnel creates (the landing hero and the public CV Health
 * review both hand over 24-hour tokens).
 */
export const RESUME_HANDOFF_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export type StoredResumeHandoffStaleReason =
  /** A pre-aging build stored the bare token: its age is unknown. */
  | 'legacy'
  /** Older than RESUME_HANDOFF_MAX_AGE_MS. */
  | 'aged'
  /** Past the expiry the server reported when the token was saved. */
  | 'expired'
  /** Not a record this build can read. */
  | 'malformed';

export type StoredResumeHandoffRead =
  | { status: 'none' }
  | {
      status: 'fresh';
      token: string;
      savedAt: number;
      expiresAt: number | null;
    }
  | { status: 'stale'; reason: StoredResumeHandoffStaleReason };

type StoredResumeHandoffRecord = {
  token: string;
  savedAt: number;
  expiresAt?: number | null;
};

let nativeRawCache: string | null = null;
// SecureStore writes and deletes run in order: a save followed closely by a
// clear (or the reverse) must settle in call order, which fire-and-forget
// calls did not guarantee.
let nativeStorageChain: Promise<void> = Promise.resolve();

function enqueueNativeStorage(operation: () => Promise<unknown>) {
  const run = nativeStorageChain.then(async () => {
    try {
      await operation();
    } catch {
      // Best effort only: the in-memory cache already reflects the intent.
    }
  });
  nativeStorageChain = run;
  return run;
}

export function normalizeResumeHandoffToken(value?: string | string[] | null) {
  const token = (Array.isArray(value) ? value[0] : value)?.trim() ?? '';
  return RESUME_HANDOFF_TOKEN_REGEX.test(token) ? token : null;
}

function toEpochMs(value: string | number | Date | null | undefined) {
  if (value === null || value === undefined) return null;
  const ms =
    value instanceof Date
      ? value.getTime()
      : typeof value === 'number'
        ? value
        : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Pure reading of a stored value. Exported for tests and for callers that
 * want the reason a token was not trusted.
 */
export function parseStoredResumeHandoff(
  raw: string | null | undefined,
  now = Date.now(),
): StoredResumeHandoffRead {
  const value = raw?.trim() ?? '';
  if (!value) {
    return { status: 'none' };
  }

  if (RESUME_HANDOFF_TOKEN_REGEX.test(value)) {
    return { status: 'stale', reason: 'legacy' };
  }

  let record: unknown;
  try {
    record = JSON.parse(value);
  } catch {
    return { status: 'stale', reason: 'malformed' };
  }
  if (!record || typeof record !== 'object' || Array.isArray(record)) {
    return { status: 'stale', reason: 'malformed' };
  }

  const { token, savedAt, expiresAt } = record as Partial<
    Record<keyof StoredResumeHandoffRecord, unknown>
  >;
  const normalizedToken = normalizeResumeHandoffToken(
    typeof token === 'string' ? token : null,
  );
  if (
    !normalizedToken ||
    typeof savedAt !== 'number' ||
    !Number.isFinite(savedAt)
  ) {
    return { status: 'stale', reason: 'malformed' };
  }

  const expiry =
    typeof expiresAt === 'number' && Number.isFinite(expiresAt)
      ? expiresAt
      : null;
  if (expiry !== null && expiry <= now) {
    return { status: 'stale', reason: 'expired' };
  }
  if (now - savedAt > RESUME_HANDOFF_MAX_AGE_MS) {
    return { status: 'stale', reason: 'aged' };
  }

  return {
    status: 'fresh',
    token: normalizedToken,
    savedAt,
    expiresAt: expiry,
  };
}

async function readRawAsync(): Promise<string | null> {
  if (Platform.OS === 'web') {
    return readWebStorage(RESUME_HANDOFF_TOKEN_KEY, 'session');
  }

  if (nativeRawCache) {
    return nativeRawCache;
  }

  // A save or clear may still be in flight: read after it has landed.
  await nativeStorageChain;
  if (nativeRawCache) {
    return nativeRawCache;
  }

  try {
    nativeRawCache = await SecureStore.getItemAsync(RESUME_HANDOFF_TOKEN_KEY);
  } catch {
    nativeRawCache = null;
  }
  return nativeRawCache;
}

function readRawSync(): string | null {
  if (Platform.OS === 'web') {
    return readWebStorage(RESUME_HANDOFF_TOKEN_KEY, 'session');
  }

  return nativeRawCache;
}

/**
 * Remember a token the app was just handed. `expiresAt` is the server's
 * expiry when the caller knows it; without it the entry is trusted for
 * RESUME_HANDOFF_MAX_AGE_MS from now.
 */
export async function saveResumeHandoffToken(
  token: string,
  options?: { expiresAt?: string | number | Date | null },
) {
  const normalized = normalizeResumeHandoffToken(token);
  if (!normalized) return;

  const record: StoredResumeHandoffRecord = {
    token: normalized,
    savedAt: Date.now(),
    expiresAt: toEpochMs(options?.expiresAt),
  };
  const value = JSON.stringify(record);

  if (Platform.OS === 'web') {
    writeWebStorage(RESUME_HANDOFF_TOKEN_KEY, value, 'session');
    return;
  }

  nativeRawCache = value;
  await enqueueNativeStorage(() =>
    SecureStore.setItemAsync(RESUME_HANDOFF_TOKEN_KEY, value),
  );
}

export async function clearStoredResumeHandoff() {
  if (Platform.OS === 'web') {
    removeWebStorage(RESUME_HANDOFF_TOKEN_KEY, 'session');
    return;
  }

  nativeRawCache = null;
  await enqueueNativeStorage(() =>
    SecureStore.deleteItemAsync(RESUME_HANDOFF_TOKEN_KEY),
  );
}

/**
 * The stored handoff with its freshness. A stale entry is discarded here, so
 * no later read (or a cold start) can trust it; the caller only learns why.
 */
export async function readStoredResumeHandoffAsync(): Promise<StoredResumeHandoffRead> {
  const read = parseStoredResumeHandoff(await readRawAsync());
  if (read.status === 'stale') {
    await clearStoredResumeHandoff();
  }
  return read;
}

/** The pending token, or null. Stale entries are discarded, never returned. */
export async function readResumeHandoffTokenAsync() {
  const read = await readStoredResumeHandoffAsync();
  return read.status === 'fresh' ? read.token : null;
}

/**
 * Synchronous variant for render-time reads. On native it only sees what
 * this runtime has already loaded or saved; use the async read for the
 * cold-start value.
 */
export function readResumeHandoffToken() {
  const read = parseStoredResumeHandoff(readRawSync());
  if (read.status === 'stale') {
    void clearStoredResumeHandoff();
    return null;
  }
  return read.status === 'fresh' ? read.token : null;
}
